import { Action } from 'redux';
import { applyPatches, enablePatches, produce } from 'immer';
import {
  FORM_CHANGE_DOCUMENT,
  FORM_CLEAR_DOCUMENT,
  FORM_DELETE_DOCUMENT,
  FORM_REDO,
  FORM_RESET_CHANGE_DOCUMENT,
  FORM_RESET_DOCUMENT_INFO,
  FORM_RESET_TO_ORG_DOCUMENT,
  FORM_RESET_UNDO_REDO,
  FORM_SET_COMPONENT_FLAG_TEMPLATE,
  FORM_SET_DISABLE_SAVE_FIELD_DOCUMENT,
  FORM_SET_DOCUMENT,
  FORM_SET_DOCUMENT_INFO,
  FORM_SET_ORG_DOCUMENT,
  FORM_SETUP_DOCUMENT_LIST,
  FORM_UNDO,
  FORM_UPDATE_DOCUMENT,
  FORM_SET_DOCUMENT_SESSION,
  IFormDeleteDocumentAction,
  IFormDocumentChangedAction,
  IFormResetDocumentInfoAction,
  IFormResetToOrgDocumentAction,
  IFormSetComponentFlagAction,
  IFormSetDocumentAction,
  IFormSetDocumentInfoAction,
  IFormSetupDocumentListAction,
  IFormSetDisabledSaveAction,
  IFormSetDocumentSessionAction
} from '../actions';
import { getDefaultFormNewState, IFormNewState } from '../state/form-new.state';
import { ClientConstants } from '../../constants/ClientConstants';
import { ChangeSourceInfo, SharedConstants, StateChangeInfo } from '@formbird/types';

let changes = {};
let currentVersion = -1;

export function formNewReducer(state: IFormNewState = getDefaultFormNewState(), action: Action): IFormNewState {

  enablePatches();

  let shouldRecordPatches = false;
  const recordedPatches: StateChangeInfo = {};
  let recordedUndoRedo = false;
  const changedSourceInfo: ChangeSourceInfo = {};

  state = produce(state, draft => {

    switch (action.type) {

      case FORM_SETUP_DOCUMENT_LIST: {
        const typedAction = <IFormSetupDocumentListAction>action;
        const isMainDoc = typedAction.payload.isMainDoc;
        const documentListId = typedAction.payload.documentListId;
        const documentInfo = draft.documentInfo;
        if (isMainDoc) {
          const docListIds = Object.keys(documentInfo);
          docListIds.forEach(docListId => {
            if (documentInfo[docListId].isMainDoc) {
              // remove document information of previous main document
              delete documentInfo[docListId];
            }
          });
        }

        if (!draft.documentInfo[documentListId]) {
          draft.documentInfo[documentListId] = {};
        }

        draft.documentInfo[documentListId].name =
          isMainDoc ? ClientConstants.MAIN_DOCUMENT_INFO_LIST : ClientConstants.CHILD_DOCUMENT_INFO_LIST;
        draft.documentInfo[documentListId].isMainDoc = isMainDoc;
        draft.documentInfo[documentListId].documentId = null;
        draft.documentInfo[documentListId].templateId = null;
        draft.documentInfo[documentListId].altTemplateId = null;
        draft.documentInfo[documentListId].options = {};
        draft.documentInfo[documentListId].hierarchyDocInfo = {};

        break;
      }

      case FORM_SET_ORG_DOCUMENT: {
        const typedAction = <IFormSetDocumentAction>action;
        const document = typedAction.payload.document;
        draft.orgDocuments[document.documentId] = document;
        break;
      }

      case FORM_SET_DOCUMENT: {
        const typedAction = <IFormSetDocumentAction>action;
        const document = typedAction.payload.document;
        if ( typedAction.payload.versionId || document ){
          const key = typedAction.payload.versionId ? typedAction.payload.versionId : document.documentId;
          draft.documents[key] = document;
          shouldRecordPatches = true;
        }

        break;
      }

      case FORM_SET_COMPONENT_FLAG_TEMPLATE: {
        const typedAction = <IFormSetComponentFlagAction> action;
        const template = draft.documents[typedAction.payload.templateId];

        template.components.filter(tplItem => tplItem.name === typedAction.payload.fieldName).forEach(tplItem => {
          tplItem[typedAction.payload.flagName] = typedAction.payload.flagValue;
        });

        shouldRecordPatches = true;

        break;
      }

      case FORM_CLEAR_DOCUMENT: {
        const documentIds = Object.keys(draft.documents);
        documentIds.forEach(docId => {
          if (draft.documents[docId].systemHeader.systemType === SharedConstants.SYSTEM_TYPE_DOCUMENT) {
            // remove document from store
            delete draft.documents[docId];
          }
        });

        shouldRecordPatches = true;

        break;
      }

      case FORM_DELETE_DOCUMENT: {
        const typedAction = <IFormDeleteDocumentAction> action;
        const documentId = typedAction.payload.documentId;
        delete draft.documents[documentId];
        delete draft.orgDocuments[documentId];

        shouldRecordPatches = true;

        break;
      }

      case FORM_RESET_TO_ORG_DOCUMENT: {
        const typedAction = <IFormResetToOrgDocumentAction> action;
        const documentId = typedAction.payload.documentId;
        draft.documents[documentId] = draft.orgDocuments[documentId];

        shouldRecordPatches = true;

        break;
      }

      case FORM_SET_DOCUMENT_INFO: {
        const typedAction = <IFormSetDocumentInfoAction>action;

        const documentInfo = typedAction.payload.documentInfo;
        const unsavedDocListId = typedAction.payload.unsavedDocumentListId;

        const currentDocumentInfo = draft.documentInfo[unsavedDocListId];

        const keys = Object.keys(documentInfo);
        keys.forEach(key => {

          if (key !== 'hierarchyInfo' && key !== 'options') {
            currentDocumentInfo[key] = documentInfo[key];

          } else if (key === 'hierarchyInfo') {

            if (!currentDocumentInfo.hierarchyInfo) {
              currentDocumentInfo.hierarchyInfo = {};
            }

            const hiKeys = Object.keys(documentInfo.hierarchyInfo);
            hiKeys.forEach(key1 => {
              currentDocumentInfo.hierarchyInfo[key1] = documentInfo.hierarchyInfo[key1];
            });

          } else if (key === 'options') {

            if (!currentDocumentInfo.options) {
              currentDocumentInfo.options = {};
            }

            const optKeys = Object.keys(documentInfo.options);
            optKeys.forEach(key2 => {
              currentDocumentInfo.options[key2] = documentInfo.options[key2];
            });

          }
        });

        break;
      }

      case FORM_RESET_DOCUMENT_INFO: {
        const typedAction = <IFormResetDocumentInfoAction> action;
        draft.documentInfo[typedAction.payload.unsavedDocumentListId] = {};
        break;
      }

      case FORM_UPDATE_DOCUMENT: {
        const typedAction = <IFormDocumentChangedAction> action;
        const changedInfo = typedAction.payload.documentChangedInfo;

        const documentId = changedInfo.documentId;
        const document = draft.documents[documentId];
        const newValues = changedInfo.newValue;
        if (newValues && newValues.length) {
          newValues.forEach(item => {
            document[item.fieldName] = item.remoteValue;
          });

          changedSourceInfo.documentId = documentId;
          changedSourceInfo.fieldName = changedInfo.fieldName;
          changedSourceInfo.type = changedInfo.sourceType;
          changedSourceInfo.controlName = changedInfo.controlName;
          shouldRecordPatches = true;
        }

        break;
      }

      case FORM_CHANGE_DOCUMENT: {
        const typedAction = <IFormDocumentChangedAction>action;
        const changedInfo = typedAction.payload.documentChangedInfo;

        const documentId = changedInfo.documentId;
        const document = draft.documents[documentId];
        const fieldName = changedInfo.fieldName;

        changedInfo.oldValue = document[fieldName];
        document[fieldName] = changedInfo.newValue;

        if (!changedInfo.isInitValue) {
          draft.canUndo = true;
          draft.canRedo = false;

          draft.documentChangedInfo = changedInfo;
        }

        changedSourceInfo.documentId = documentId;
        changedSourceInfo.fieldName = fieldName;
        changedSourceInfo.type = changedInfo.sourceType;
        changedSourceInfo.controlName = changedInfo.controlName;
        shouldRecordPatches = true;

        const template = draft.documents[document.systemHeader.templateId];
        if (template && template.components) {
          const tplItem = template.components.filter(item => {
            return item.name === fieldName;
          })[0];

          if (tplItem.disableSave === true) {
            if (!draft.recordedDisableSaveFieldValues[documentId]) {
              draft.recordedDisableSaveFieldValues[documentId] = {};
            }
            draft.recordedDisableSaveFieldValues[documentId][fieldName] = document[fieldName];
          }
        }

        break;
      }

      case FORM_RESET_CHANGE_DOCUMENT: {
        delete draft.documentChangedInfo;
        break;
      }

      case FORM_UNDO: {

        shouldRecordPatches = true;
        recordedUndoRedo = true;

        const newState = <IFormNewState> applyPatches(state, changes[currentVersion--].undo);

        return produce(
          newState,
          newDraft => {
            newDraft.canUndo = changes.hasOwnProperty(currentVersion);
            newDraft.canRedo = true;
          }
        );

      }

      case FORM_REDO: {

        shouldRecordPatches = true;
        recordedUndoRedo = true;

        const newState = <IFormNewState> applyPatches(state, changes[++currentVersion].redo);
        return produce(
          newState,
          newDraft => {
            newDraft.canUndo = true;
            newDraft.canRedo = changes.hasOwnProperty(currentVersion + 1);
          }
        );

      }

      case FORM_RESET_UNDO_REDO: {

        currentVersion = -1;
        changes = {};
        draft.canRedo = false;
        draft.canUndo = false;

        break;
      }

      case FORM_SET_DISABLE_SAVE_FIELD_DOCUMENT: {
        const typedAction = <IFormSetDisabledSaveAction>action;
        const documentId = typedAction.payload.documentId;
        const value = typedAction.payload.disabledFields;

        if (Object.keys(value).length) {
          draft.recordedDisableSaveFieldValues[documentId] = value;
        } else {
          delete draft.recordedDisableSaveFieldValues[documentId];
        }

        break;
      }

      case FORM_SET_DOCUMENT_SESSION: {
        const typedAction = <IFormSetDocumentSessionAction>action;
        const document = typedAction.payload.documentSession;
        const key = typedAction.payload.documentId;

        draft.documentSession[key] = document;

        break;
      }

      default: {
        return state;
      }
    }
  }, (patches, inversePatches) => {

    if (action.type === FORM_CHANGE_DOCUMENT) {

      const typedAction = <IFormDocumentChangedAction> action;
      if (!typedAction.payload.documentChangedInfo.isInitValue) {

        currentVersion++;

        changes[currentVersion] = {
          redo: patches,
          undo: inversePatches
        };
      }
    }

    if (shouldRecordPatches) {
      recordedPatches.patches = patches;
      recordedPatches.inversePatches = inversePatches;
      recordedPatches.recordedUndoRedo = recordedUndoRedo;
      recordedPatches.changedSourceInfo = changedSourceInfo;
    }

  });

  // set the changes to state from reducer so if any changes they should be triggered instead of listening whole state.
  return { ...state, recordedStateChanges: recordedPatches };

}
